import EMBEDS from './rawHtmlEmbeds.json';

/**
 * Embed hosts a Raw HTML section may load (Dawn, 2026-10-20: "allowed embeds live"). The list
 * itself is rawHtmlEmbeds.json, read here and by apps/tenant-sites next.config.mjs for the CSP,
 * so the sanitizer, the Studio note and the CSP can't drift apart.
 *
 * A URL passes only when, parsed by WHATWG `URL`:
 *   - it is https, with no user name or password and no explicit port other than 443 (`URL`
 *     drops a default port, so `:443` passes and `:8443` doesn't);
 *   - its origin is listed exactly (`URL` lowercases the host; a trailing dot, an IP, a look-alike
 *     subdomain or any other host is simply not on the list, so it fails closed);
 *   - when the entry lists paths, its path is one of them or below one (`/maps/embed`,
 *     `/maps/embed/v1/...`, never `/maps/embedx` or `/url`).
 * Inline <script> and on* handlers are never allowed, whoever wrote the section.
 */
export interface EmbedEntry {
  origin: string;
  paths?: string[];
}

export const EMBED_FRAMES: readonly EmbedEntry[] = EMBEDS.frames;
export const EMBED_SCRIPTS: readonly EmbedEntry[] = EMBEDS.scripts;

/** Kept for callers that only need the host names. */
export const EMBED_IFRAME_HOSTS: readonly string[] = EMBED_FRAMES.map((e) => new URL(e.origin).hostname);
export const EMBED_SCRIPT_HOSTS: readonly string[] = EMBED_SCRIPTS.map((e) => new URL(e.origin).hostname);

/** The URL, parsed and normalised, when it is a clean https URL; else null. */
export function parseEmbedUrl(raw: string): URL | null {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' || u.username || u.password || u.port !== '') return null;
  return u;
}

/** The URL's host when it is a clean https URL, else null (relative, http:, javascript:, a port …). */
export function embedHost(raw: string): string | null {
  return parseEmbedUrl(raw)?.hostname ?? null;
}

function matches(list: readonly EmbedEntry[], raw: string): boolean {
  const u = parseEmbedUrl(raw);
  if (!u) return false;
  const entry = list.find((e) => e.origin === u.origin);
  if (!entry) return false;
  if (!entry.paths) return true;
  return entry.paths.some((p) => u.pathname === p || u.pathname.startsWith(`${p}/`));
}

export function isAllowedIframeSrc(src: string): boolean {
  return matches(EMBED_FRAMES, src);
}

export function isAllowedScriptSrc(src: string): boolean {
  return matches(EMBED_SCRIPTS, src);
}

/** The normalised full URL used to load (and dedupe) an allowed script, else null. */
export function embedScriptUrl(src: string): string | null {
  return isAllowedScriptSrc(src) ? parseEmbedUrl(src)!.href : null;
}
